/**
 * Client session state for the Perilous Games login. The server's
 * /api/login endpoints (backed by the website proxy + APIAccount) do the
 * real work; this module just holds the returned session and re-validates
 * it on later visits. Guests have no session at all.
 */

export interface Session {
  email: string;
  displayName: string;
  /** APIAccount opaque bearer token (expires ~1 day). */
  token: string;
}

const STORAGE_KEY: string = "mallmart.session";

export function loadStoredSession(): Session | null {
  const raw: string | null = localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.email !== "string" || parsed.email === "" ||
      typeof parsed.token !== "string" || parsed.token === "" ||
      typeof parsed.displayName !== "string"
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { email: parsed.email, displayName: parsed.displayName, token: parsed.token };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function storeSession(session: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

async function postLogin(url: string, body: Record<string, unknown>): Promise<Session> {
  const res: Response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error("EMAIL_VERIFICATION_REQUIRED");
    }
    throw new Error(res.status === 401 ? "SIGNIN_REJECTED" : `Login failed (${res.status})`);
  }
  const data = (await res.json()) as { user: Session };
  return data.user;
}

/** Exchange a Google ID token for a session and persist it. */
export async function loginWithGoogle(serverHttpUrl: string, googleIdToken: string): Promise<Session> {
  const session: Session = await postLogin(`${serverHttpUrl}/api/login/google`, { googleIdToken });
  storeSession(session);
  return session;
}

/** Perilous email + password login; persists the session like Google. */
export async function loginWithPassword(
  serverHttpUrl: string,
  email: string,
  password: string
): Promise<Session> {
  const session: Session = await postLogin(`${serverHttpUrl}/api/login/password`, {
    email,
    password,
  });
  storeSession(session);
  return session;
}

/** Silently re-validate a stored session. Returns the refreshed session, or
 *  null when it is stale (cleared from storage) or the auth service is down. */
export async function validateStoredSession(serverHttpUrl: string): Promise<Session | null> {
  const stored: Session | null = loadStoredSession();
  if (stored === null) {
    return null;
  }
  try {
    const session: Session = await postLogin(`${serverHttpUrl}/api/login/token`, {
      email: stored.email,
      token: stored.token,
    });
    storeSession(session);
    return session;
  } catch (err: unknown) {
    if (err instanceof Error && err.message === "SIGNIN_REJECTED") {
      clearSession();
    }
    return null;
  }
}
